import type { OrderDrawerIdentifier } from "@features/orders-features/components/order-drawer";
import { useId, useMemo, useState } from "react";
import { createSavedCurrencyFormatter } from "shared-ui/src/functions/currency";
import { useClaims } from "../../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../../auth/authorizationTypes";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { Theme } from "@mui/material/styles";
import { useGetOrderInvoices } from "@features/orders-features/hooks/useGetOrderInvoices";
import { OrderInvoiceDto } from "@lib/ShiOneClient";
import { ReceiptLong } from "@mui/icons-material";
import { formatOrderDate } from "@features/orders-features/functions/formatOrderDate";
import darkPalette from "shared-ui/src/theme/darkPalette";
import shiPalette from "shared-ui/src/theme/shiPalette";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useOrderTelemetrySection } from "../orderTelemetrySection";

function InvoiceList({ data, currencyCode }: { data: OrderInvoiceDto[]; currencyCode?: string | null }) {
  const formatter = useMemo(() => createSavedCurrencyFormatter(currencyCode), [currencyCode]);
  const hasMultipleInvoices = data.length > 1;

  return (
    <List disablePadding aria-label="Order invoices" sx={{ px: 2, py: 1 }}>
      {data.map((invoice, index) => {
        if (!invoice.invoiceNumber) {
          return null;
        }

        const invoicedOn = invoice.invoicedOn
          ? formatOrderDate(invoice.invoicedOn)
          : undefined;
        const validInvoicedOn = invoicedOn && invoicedOn !== "—";

        const isLast = index === data.length - 1;
        const boxSx = hasMultipleInvoices
          ? {
              mb: isLast ? 0 : -1,
              borderRadius: 1,
              bgcolor: (theme: Theme) =>
                theme.palette.mode === "dark"
                  ? darkPalette.background.default
                  : shiPalette.background.default,
              px: 2,
              py: 1,
            }
          : {};

        return (
          <ListItem
            key={invoice.invoiceId ?? `${invoice.invoiceNumber}-${index}`}
            disableGutters
            sx={{ display: "block" }}
          >
            <Box sx={{ ...boxSx }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <ReceiptLong fontSize="small" color="action" />
                  </ListItemIcon>
                  <Typography variant="body1">
                    {invoice.invoiceNumber}
                  </Typography>
                  {validInvoicedOn && (
                    <Typography variant="body2" color="text.secondary">
                      {invoicedOn}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="body1" color="text.primary">
                  {formatter.format(invoice.totalValue ?? 0)}
                </Typography>
              </Box>
            </Box>
          </ListItem>
        );
      })}
    </List>
  );
}

interface OrderDrawerInvoicesProps extends OrderDrawerIdentifier {
  custPONumber?: string;
  currencyCode?: string | null;
  disableGutters?: boolean;
  /** Opens the email invoices dialog; the drawer owns it so a refresh can restore it. */
  onEmailInvoices?: () => void;
}

export function OrderDrawerInvoices({
  biOrderId,
  salesOrder,
  custPONumber,
  currencyCode,
  disableGutters = false,
  onEmailInvoices,
}: Readonly<OrderDrawerInvoicesProps>) {
  const [expanded, setExpanded] = useState(true);
  const invoiceSectionId = useId();
  const invoicePanelId = useId();
  const { hasPermission } = useClaims();
  const canEmailInvoice =
    hasPermission(ShiOnePermission.orderSearchOrgUnit) && !!onEmailInvoices;
  const telemetrySection = useOrderTelemetrySection();

  const {
    data: invoices,
    isLoading,
    isError,
  } = useGetOrderInvoices({
    biOrderId: biOrderId ?? undefined,
    orderNumber: salesOrder,
    custPONumber
  });

  const hasInvoices = !!biOrderId && (invoices?.length ?? 0) > 0;

  if (isLoading || isError || !hasInvoices) {
    return null;
  }

  return (
    <>
      <Card elevation={1} sx={{ mx: disableGutters ? 0 : 2, my: 0, borderRadius: 1 }}>
        <CardActionArea
          id={invoiceSectionId}
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-controls={invoicePanelId}
          aria-label="Toggle invoice section"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography
            component="h3"
            variant="subtitle1"
            sx={{ textTransform: "uppercase" }}
          >
            Invoice
          </Typography>
          <ExpandMoreIcon
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
              color: "action.active",
            }}
            aria-hidden="true"
          />
        </CardActionArea>
        <Collapse
          id={invoicePanelId}
          in={expanded}
          role="region"
          aria-labelledby={invoiceSectionId}
        >
          <InvoiceList data={invoices ?? []} currencyCode={currencyCode} />
          <Divider sx={{ borderStyle: "solid" }} />
          {canEmailInvoice && (
            <Box sx={{ px: 2, py: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onEmailInvoices?.();
                }}
                {...telemetryProps({
                  name: "orders_email_invoice",
                  section: telemetrySection,
                  label: "Email invoice",
                  ...(salesOrder ? { id: String(salesOrder) } : {}),
                })}
              >
                EMAIL INVOICE
              </Button>
            </Box>
          )}
        </Collapse>
      </Card>
    </>
  );
}
